import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { toast } from "../ui/use-toast";
import { Alert, AlertDescription } from "../ui/alert";
import { AlertCircle, Info } from "lucide-react";
import { Button } from "../ui/button";

// File size constants
const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB
const RECOMMENDED_FILE_SIZE = 2 * 1024 * 1024; // 2MB

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function FileUploader({ onChange }) {
  const { t } = useTranslation('common');
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleAddFiles = (newFiles) => {
    const selectedFiles = Array.from(newFiles || []);
    
    // Filter out files that are too large
    const validFiles = [];
    const invalidFiles = [];
    
    selectedFiles.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        invalidFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });
    
    // Show error for invalid files
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(file => {
        toast({
          title: t('file_size_limit_exceeded', { filename: file.name }),
          description: t('file_size_limit_description', { maxSize: formatFileSize(MAX_FILE_SIZE) }),
          variant: "destructive",
        });
      });
    }
    
    // Add only valid files
    if (validFiles.length > 0) {
      setFiles((prev) => {
        const existingNames = new Set(prev.map((f) => f.name));
        const uniqueNewFiles = validFiles.filter((f) => !existingNames.has(f.name));
        const updated = [...prev, ...uniqueNewFiles];
        onChange && onChange(updated);
        return updated;
      });
    }
  };

  const handleInputChange = (e) => {
    handleAddFiles(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      handleAddFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = () => {
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prev) => {
      const updated = prev.filter((_, i) => i !== indexToRemove);
      onChange && onChange(updated);
      return updated;
    });
  };

  return (
    <div>
      <div
        className={clsx(
          "border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors",
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
        )}
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
      >
        <div className="text-sm text-gray-500 h-[100px] flex items-center justify-center">
          {t('drag_drop_files')} <span className="text-blue-600 underline cursor-pointer pl-1">{t('browse')}</span>
        </div>
      </div>
      
      {/* File size recommendation alert */}
      <Alert className="mt-3 border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          {t('file_size_recommendation')}
        </AlertDescription>
      </Alert>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.png,.jpg"
        onChange={handleInputChange}
        className="hidden"
      />
      {files.length > 0 && (
        <ul className="mt-2 text-sm text-gray-700 space-y-1">
          {files.map((file, index) => (
            <li key={index} className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="truncate max-w-[200px]">{file.name}</div>
                <div className={`text-xs ${file.size > RECOMMENDED_FILE_SIZE ? 'text-amber-600' : 'text-gray-500'}`}>
                  {formatFileSize(file.size)}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() => handleRemoveFile(index)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}